import React from 'react'

import { Checkbox } from '@/app/component/atom/form'
import ActionsBox from '@/app/module/campaigns/component/actions/actions-box'
import { GetImageProps, GetVoiceProps } from '@/app/module/campaigns/component/item/steps/content/part-config-props'
import { SMS_ACTION_DEFAULT_SENDERID } from '@/app/module/campaigns/definitions'
import { CampaignTemplateType, Language, LocalAction, PartFamily, SenderIdOption } from '@/app/module/campaigns/types'
import { Action } from '@/app/module/campaigns/types/schemas/actions/all-actions'
import { getActionOptionsForCampaign } from '@/app/module/campaigns/utils/actions/options'
import { ActionContext } from '@/app/module/campaigns/utils/actions/types'
import { unwrapActions, wrapActions } from '@/app/module/campaigns/utils/actions/wrap'
import { CountryTimezone, FilesType, PersonalizationType, TimezoneWithCountryCodeType } from '@/app/types'

export type CallResultKey = 'answered' | 'notAnswered' | 'error'

type Props = {
  actions: Action[]
  campaignType: CampaignTemplateType
  countriesTimezones: CountryTimezone[]
  countryCode: string
  defaultLanguage: string
  files: FilesType
  languages: Language[]
  orgId: number
  personalizationList: PersonalizationType
  resultKey: CallResultKey
  senderIds: SenderIdOption[]
  timezones: TimezoneWithCountryCodeType
  timezone: string
  token: string
  whatsappSenderIds: SenderIdOption[]

  changeHandler: (payload: { value: Action[]; result: CallResultKey }) => void
  getImageProps: GetImageProps
  getVoiceProps: GetVoiceProps
}

const resultMap: Record<CallResultKey, string> = {
  answered: 'Answered',
  notAnswered: 'Not answered',
  error: 'Error',
}

const actionContextMap: Record<
  CallResultKey,
  ActionContext.CallAnswered | ActionContext.CallNotAnswered | ActionContext.CallError
> = {
  answered: ActionContext.CallAnswered,
  notAnswered: ActionContext.CallNotAnswered,
  error: ActionContext.CallError,
}

export function CallResultsActions({
  actions: propActions,
  campaignType,
  countriesTimezones,
  countryCode,
  defaultLanguage,
  files,
  languages,
  orgId,
  personalizationList,
  resultKey,
  senderIds,
  timezones,
  timezone,
  token,
  whatsappSenderIds,
  changeHandler,
  getImageProps,
  getVoiceProps,
}: Props) {
  const actions = React.useMemo(() => unwrapActions(propActions), [propActions])
  const [selected, setSelected] = React.useState(false)

  const actionOptions = React.useMemo(
    () =>
      getActionOptionsForCampaign({
        campaignType,
        partType: PartFamily.Voice,
        actionNamespace: actionContextMap[resultKey],
      }),
    [campaignType, resultKey],
  )

  React.useEffect(() => {
    if (actions.length > 0) {
      setSelected(true)
    }
  }, [actions])

  const handleActionsChange = React.useCallback(
    (newActions: LocalAction[]) => changeHandler({ value: wrapActions(newActions || []), result: resultKey }),
    [resultKey, changeHandler],
  )
  const handleToggle = React.useCallback(
    ({ value }: { value: string }) => {
      const toBeSelected = value === 'true'
      if (!toBeSelected) {
        handleActionsChange([])
      }
      setSelected(toBeSelected)
    },
    [handleActionsChange],
  )

  return (
    <>
      <Checkbox
        editable
        label={resultMap[resultKey]}
        name={resultKey}
        value={selected.toString()}
        onChange={handleToggle}
      />
      {selected && (
        <ActionsBox
          actionOptions={actionOptions}
          actions={actions}
          countriesTimezones={countriesTimezones}
          countryCode={countryCode}
          defaultLanguage={defaultLanguage}
          defaultSenderId={SMS_ACTION_DEFAULT_SENDERID.voiceQuestion}
          files={files}
          hasTranscribe={false}
          hideFinalActions={campaignType === CampaignTemplateType.VoiceSurvey}
          index={0}
          languages={languages}
          orgId={orgId}
          personalizationList={personalizationList}
          senderIds={senderIds}
          timezones={timezones}
          timezone={timezone}
          token={token}
          transcribeEnabled={false}
          updateContactConfig={updateContactConfig}
          voiceId={`call-result-${resultKey}`}
          whatsappSenderIds={whatsappSenderIds}
          getImageProps={getImageProps}
          getVoiceProps={getVoiceProps}
          onChange={handleActionsChange}
        />
      )}
    </>
  )
}

export default CallResultsActions

const updateContactConfig = {
  invalid: false,
  question: false,
  openResponse: false,
}
